/**
 * Artifact-kind sniffing utilities for the drag-and-drop pre-flight layer.
 *
 * Spec: LAYER_LIFECYCLE_AND_PORTABLE_LAYER_AUDIT.md §10.2
 * Phase: LAYER-LIFECYCLE-ALIGNMENT Phase E
 *
 * All functions are pure — no DOM access, no network, no side effects.
 * Priority order when multiple signals are available:
 *   1. Magic bytes  (most authoritative — the file says what it is)
 *   2. MIME type
 *   3. URL pattern  (suffix / filename in URL)
 *   4. File extension
 */

import type { ArtifactKind } from './preflightTypes';

// ─── Extension → kind mapping ────────────────────────────────────────────────

/**
 * Lower-cased extension (without leading dot) → ArtifactKind.
 *
 * Compound extensions (e.g. ".copc.laz") are handled by checking the full
 * suffix before falling back to the last segment.
 */
const EXTENSION_MAP: Record<string, ArtifactKind> = {
  // Raster
  tif: 'cog',
  tiff: 'cog',
  // Point clouds
  'copc.laz': 'copc',
  copc: 'copc',
  las: 'las',
  laz: 'laz',
  e57: 'e57',
  // Vector
  geojson: 'geojson',
  shp: 'shp',
  json: 'tileset3d',  // refined by magic-byte check in sniffArtifact
  fgb: 'flatgeobuf',
  parquet: 'geoparquet',
  dxf: 'dxf',
  // 3D / BIM
  glb: 'glb',
  gltf: 'glb',
  ifc: 'ifc-glb',
  // Tile formats
  pmtiles: 'pmtiles',
  // Portable layer
  glayer: 'glayer',
} as const;

// ─── MIME type → kind mapping ────────────────────────────────────────────────

const MIME_MAP: Record<string, ArtifactKind> = {
  'image/tiff': 'cog',
  'image/geo+tiff': 'cog',
  'application/gzip': 'laz',           // common MIME for .laz
  'application/vnd.laszip': 'laz',
  'model/gltf-binary': 'glb',
  'model/gltf+json': 'glb',
  'application/geo+json': 'geojson',
  'application/json': 'tileset3d',     // refined by magic-byte check
  'application/vnd.geosoft.glayer+zip': 'glayer',
  'application/x-parquet': 'geoparquet',
  'application/octet-stream': 'unknown',
} as const;

// ─── Magic byte signatures ────────────────────────────────────────────────────

/** 4-byte GLB header: magic `0x676C5446` ("glTF"), version 2 uint32-LE. */
const GLB_MAGIC = [0x67, 0x6c, 0x54, 0x46] as const;     // "glTF"

/** LAS 1.x public file header signature: "LASF" (4 bytes). */
const LAS_MAGIC = [0x4c, 0x41, 0x53, 0x46] as const;     // "LASF"

/** ZIP local file header signature: PK\x03\x04. Used for .glayer and .ifc archives. */
const ZIP_MAGIC = [0x50, 0x4b, 0x03, 0x04] as const;     // "PK\x03\x04"

/** E57 file signature: "ASTM-E57" prefix in first 8 bytes. */
const E57_MAGIC = [0x41, 0x53, 0x54, 0x4d] as const;     // "ASTM"

function bytesMatch(prefix: Uint8Array, pattern: readonly number[], offset = 0): boolean {
  if (prefix.length < offset + pattern.length) return false;
  return pattern.every((byte, i) => prefix[offset + i] === byte);
}

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Classify an artifact by file extension.
 *
 * Handles compound extensions like ".copc.laz" by checking the full
 * lower-cased basename before the last segment only.
 */
export function sniffByExtension(filename: string): ArtifactKind {
  const lower = filename.toLowerCase();
  // Check compound extension first (e.g. "cloud.copc.laz" → "copc.laz")
  const dotIndex = lower.indexOf('.');
  if (dotIndex !== -1) {
    const fullExt = lower.slice(dotIndex + 1);
    if (fullExt in EXTENSION_MAP) {
      return EXTENSION_MAP[fullExt];
    }
    // Check last segment only (e.g. "file.laz")
    const lastDot = lower.lastIndexOf('.');
    if (lastDot !== -1) {
      const lastExt = lower.slice(lastDot + 1);
      if (lastExt in EXTENSION_MAP) {
        return EXTENSION_MAP[lastExt];
      }
    }
  }
  return 'unknown';
}

/**
 * Classify an artifact by inspecting its first bytes.
 *
 * Returns `null` when the magic bytes do not match any known signature —
 * the caller should fall through to extension / MIME sniffing.
 *
 * Byte signatures checked:
 * - GLB: `glTF` (4 bytes) → `glb`
 * - LAS/LAZ: `LASF` (4 bytes) → `las` (LAZ variants refine to `laz` /
 *   `copc` via extension if needed, but at the byte level it is `las`)
 * - ZIP (`PK\x03\x04`): interpreted as `glayer` (the primary ZIP-based
 *   format in this platform; IFC is also ZIP but is caught by extension)
 * - E57: `ASTM` prefix → `e57`
 * - JSON starting with `{` and containing `"asset"` → `tileset3d`
 */
export function sniffByMagicBytes(prefix: Uint8Array): ArtifactKind | null {
  if (prefix.length === 0) return null;

  if (bytesMatch(prefix, GLB_MAGIC)) return 'glb';
  if (bytesMatch(prefix, LAS_MAGIC)) return 'las';
  if (bytesMatch(prefix, E57_MAGIC)) return 'e57';
  if (bytesMatch(prefix, ZIP_MAGIC)) return 'glayer';

  // Attempt JSON parse for tileset.json detection: look for "asset" key
  // in the first 512 bytes (avoids decoding a huge file).
  if (prefix[0] === 0x7b /* '{' */) {
    const snippet = new TextDecoder('utf-8', { fatal: false }).decode(
      prefix.subarray(0, Math.min(prefix.length, 512)),
    );
    // 3D Tiles tileset.json must have an "asset" object with "version".
    if (snippet.includes('"asset"') && snippet.includes('"version"')) {
      return 'tileset3d';
    }
  }

  return null;
}

/**
 * Classify an artifact by URL pattern.
 *
 * Matches well-known suffixes and filenames in the URL path.
 * Returns `null` when no pattern matches.
 */
export function sniffByUrl(url: string): ArtifactKind | null {
  try {
    // Strip query string and fragment for extension matching.
    const pathname = new URL(url).pathname.toLowerCase();
    // Tileset.json — must be checked before generic .json
    if (pathname.endsWith('/tileset.json') || pathname.endsWith('tileset.json')) {
      return 'tileset3d';
    }
    if (pathname.endsWith('.pmtiles')) return 'pmtiles';
    if (pathname.endsWith('.glayer')) return 'glayer';
    if (pathname.endsWith('.copc.laz')) return 'copc';
    if (pathname.endsWith('.laz')) return 'laz';
    if (pathname.endsWith('.las')) return 'las';
    if (pathname.endsWith('.glb')) return 'glb';
    if (pathname.endsWith('.gltf')) return 'glb';
    if (pathname.endsWith('.tif') || pathname.endsWith('.tiff')) return 'cog';
    if (pathname.endsWith('.geojson')) return 'geojson';
    if (pathname.endsWith('.shp')) return 'shp';
    if (pathname.endsWith('.fgb')) return 'flatgeobuf';
    if (pathname.endsWith('.parquet')) return 'geoparquet';
    if (pathname.endsWith('.dxf')) return 'dxf';
    if (pathname.endsWith('.ifc')) return 'ifc-glb';
    if (pathname.endsWith('.e57')) return 'e57';
    // Potree — metadata.json at a well-known path
    if (pathname.endsWith('/metadata.json')) return 'potree';
  } catch {
    // Malformed URL — fall through.
  }
  return null;
}

/**
 * Classify an artifact by MIME type.
 *
 * Returns `null` when the MIME type is not in the known set.
 */
export function sniffByMimeType(mime: string): ArtifactKind | null {
  const normalised = mime.toLowerCase().split(';')[0].trim();
  return (MIME_MAP[normalised] as ArtifactKind | undefined) ?? null;
}

export interface SniffInput {
  filename?: string;
  firstBytes?: Uint8Array;
  url?: string;
  mime?: string;
}

/**
 * Composite artifact-kind classifier.
 *
 * Priority:
 *   1. Magic bytes  — most authoritative; a GLB with a wrong extension is
 *      still a GLB.
 *   2. MIME type    — provided by the browser for `dataTransfer.files[]`.
 *   3. URL pattern  — for remote drops and `text/uri-list` sources.
 *   4. File name    — last resort; extension only.
 *
 * Conflict resolution: when magic bytes produce a concrete (non-null) answer,
 * that answer wins even if extension or MIME disagree.
 */
export function sniffArtifact(input: SniffInput): ArtifactKind {
  // 1. Magic bytes — highest confidence.
  if (input.firstBytes && input.firstBytes.length > 0) {
    const byMagic = sniffByMagicBytes(input.firstBytes);
    if (byMagic !== null) {
      // Special case: ZIP magic could be .ifc (IFC files are zip-based).
      // If the filename extension says .ifc, respect that over the generic glayer.
      if (byMagic === 'glayer' && input.filename) {
        const byExt = sniffByExtension(input.filename);
        if (byExt === 'ifc-glb') return 'ifc-glb';
      }
      return byMagic;
    }
  }

  // 2. MIME type.
  if (input.mime) {
    const byMime = sniffByMimeType(input.mime);
    if (byMime !== null && byMime !== 'unknown') return byMime;
  }

  // 3. URL.
  if (input.url) {
    const byUrl = sniffByUrl(input.url);
    if (byUrl !== null) return byUrl;
  }

  // 4. Filename extension.
  if (input.filename) {
    return sniffByExtension(input.filename);
  }

  return 'unknown';
}
